import { useState, useEffect, Fragment } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Tabs,
  Tab,
  Autocomplete,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Collapse,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PageBreadcrumbs from "../components/PageBreadcrumbs";
import { useUploadResults } from "../hooks/useUploadResults";
import { useMergedPreview } from "../hooks/useMergedPreview";
import { STATUS as ResultGroupStatus } from "manager-common";

/** USEF section codes commonly used for classes that merge technical + quality marks. */
const SUGGESTED_MERGE_SECTION_CODES = ["1562", "1583", "1579", "1580", "9980"];

/** Dressage ring judge positions (single letter, uppercase in UI and API). */
const JUDGE_POSITION_OPTIONS = ["E", "H", "C", "M", "B"];

function formatScoreOrPercent3(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }
  return Number(value).toFixed(3);
}

function formatJudgePositionLetter(position) {
  if (position == null || String(position).trim() === "") return "—";
  return String(position).trim().toUpperCase();
}

function normalizeRidePlacingsMap(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  return Object.fromEntries(
    Object.entries(raw).map(([k, v]) => [String(k), String(v ?? "").trim()])
  );
}

/** Per-ride placings persisted on the technical merge in Dynamo. */
function ridePlacingsFromTechGroupMerge(merge) {
  if (!merge || typeof merge !== "object") return {};
  return normalizeRidePlacingsMap(merge.placingsByRider);
}

function getActiveMergedClasses(mergedClasses, deletedMergeClassIds) {
  return mergedClasses.filter(
    (mc) =>
      !deletedMergeClassIds.has(mc.technicalAssessment.id) &&
      !deletedMergeClassIds.has(mc.qualityMarking.id)
  );
}

function hasMergeMissingPlacing(mergedClasses, deletedMergeClassIds) {
  return getActiveMergedClasses(mergedClasses, deletedMergeClassIds).some((mc) => {
    const rp = mc.ridePlacings || {};
    const keys = Object.keys(rp);
    if (keys.length === 0) return true;
    return keys.some((k) => String(rp[k] ?? "").trim() === "");
  });
}

function isSuggestedMergeSection(group, suggestedSectionCodes) {
  const code = group.USEFSectionCode ? String(group.USEFSectionCode).trim() : null;
  return Boolean(code && suggestedSectionCodes.includes(code));
}

function compareGroupsByClassId(a, b) {
  return String(a.classId).localeCompare(String(b.classId), undefined, { numeric: true });
}

/**
 * Merge dropdown options: all eligible groups, with suggested section codes first,
 * then other classes, each bucket sorted by class id.
 */
function buildMergeClassOptions(sortedGroups, {
  mergedClassIds,
  excludeOtherSelectionId,
  suggestedSectionCodes,
}) {
  const base = sortedGroups.filter((group) => {
    if (
      group.status === ResultGroupStatus.SKIPPED ||
      group.className === "(Did not Fill)"
    ) {
      return false;
    }
    if (excludeOtherSelectionId && group.id === excludeOtherSelectionId) {
      return false;
    }
    if (mergedClassIds.has(group.id)) {
      return false;
    }
    return true;
  });

  const suggested = base
    .filter((g) => isSuggestedMergeSection(g, suggestedSectionCodes))
    .sort(compareGroupsByClassId);
  const other = base
    .filter((g) => !isSuggestedMergeSection(g, suggestedSectionCodes))
    .sort(compareGroupsByClassId);
  return [...suggested, ...other];
}

/**
 * Only show grouped list headers when both suggested and non-suggested options exist;
 * otherwise a single flat list avoids redundant section labels.
 */
function mergeOptionsShouldUseGroupHeaders(options, suggestedSectionCodes) {
  if (options.length === 0) {
    return false;
  }
  let sawSuggested = false;
  let sawOther = false;
  for (const opt of options) {
    if (isSuggestedMergeSection(opt, suggestedSectionCodes)) {
      sawSuggested = true;
    } else {
      sawOther = true;
    }
    if (sawSuggested && sawOther) {
      return true;
    }
  }
  return false;
}

function UploadResultsPage() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  const [selectedFile, setSelectedFile] = useState(null);
  const [showForm, setShowForm] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [currentTab, setCurrentTab] = useState(0);
  const [technicalAssessment, setTechnicalAssessment] = useState(null);
  const [qualityMarking, setQualityMarking] = useState(null);
  const [judges, setJudges] = useState([]);
  const [judgePositions, setJudgePositions] = useState({});
  const [judgePositionErrors, setJudgePositionErrors] = useState(new Set());
  const [mergedClasses, setMergedClasses] = useState([]);
  const [previewRidePlacings, setPreviewRidePlacings] = useState({});
  const [ridePlacingDialogMergeId, setRidePlacingDialogMergeId] = useState(null);
  const [dialogRidePlacings, setDialogRidePlacings] = useState({});
  const [dialogPreviewRows, setDialogPreviewRows] = useState([]);
  const [dialogPlacingLoading, setDialogPlacingLoading] = useState(false);
  const [deletedMergeClassIds, setDeletedMergeClassIds] = useState(new Set());
  const [mergedPreviewResults, setMergedPreviewResults] = useState([]);
  const [expandedPreviewRows, setExpandedPreviewRows] = useState(new Set());

  const { uploading, error, uploadResponse, upload, publish, reset } =
    useUploadResults();
  const { fetchPreview, loading: loadingPreview } = useMergedPreview();

  const activeUploadResponse = uploadResponse;

  // Initialize selectedIds with all group IDs when uploadResponse is available
  // Exclude SKIPPED and PUBLISHED groups (only PUBLISHED should be unchecked by default)
  // NEW, UPLOADED, and ERROR groups are selected by default
  // (Users can still manually check published groups if they want to resubmit)
  useEffect(() => {
    if (activeUploadResponse?.groups) {
      const selectableGroups = activeUploadResponse.groups.filter(
        (group) =>
          group.status !== ResultGroupStatus.SKIPPED &&
          group.status !== ResultGroupStatus.PUBLISHED &&
          group.className !== "(Did not Fill)"
      );
      const allIds = new Set(selectableGroups.map((group) => group.id));
      setSelectedIds(allIds);
    }
  }, [activeUploadResponse]);

  // Populate mergedClasses from uploadResponse when groups have merge information
  useEffect(() => {
    if (!activeUploadResponse?.groups) {
      return;
    }

    // Reset deleted merge tracking when new upload response is received
    setDeletedMergeClassIds(new Set());

    // Find all groups with merge information
    const groupsWithMerge = activeUploadResponse.groups.filter(
      (group) => group.merge && group.merge.type === "technical"
    );

    if (groupsWithMerge.length === 0) {
      setMergedClasses([]);
      return;
    }

    // Reconstruct mergedClasses from merge data
    const reconstructedMerges = groupsWithMerge.map((techGroup) => {
      // Find the corresponding quality group
      const qualityGroup = activeUploadResponse.groups.find(
        (g) =>
          g.classId === techGroup.merge.mergeWith &&
          g.merge &&
          g.merge.type === "quality" &&
          g.merge.mergeWith === techGroup.classId
      );

      if (!qualityGroup) {
        return null;
      }

      return {
        id: `merged_${techGroup.id}_${qualityGroup.id}`,
        technicalAssessment: {
          id: techGroup.id,
          classId: techGroup.classId,
          className: techGroup.className,
        },
        qualityMarking: {
          id: qualityGroup.id,
          classId: qualityGroup.classId,
          className: qualityGroup.className,
        },
        judges: techGroup.merge.judges || [],
        ridePlacings: ridePlacingsFromTechGroupMerge(techGroup.merge),
      };
    }).filter(Boolean); // Remove null entries

    setMergedClasses(reconstructedMerges);
  }, [activeUploadResponse?.groups]);

  useEffect(() => {
    if (!mergedPreviewResults.length) {
      setPreviewRidePlacings({});
      return;
    }
    setPreviewRidePlacings((prev) => {
      const next = { ...prev };
      for (const r of mergedPreviewResults) {
        if (r.scratched) continue;
        const k = r.entryKey || r.riderUsef || "";
        if (!k) continue;
        if (!(k in next)) {
          next[k] = String(r.placing ?? "").trim();
        }
      }
      const keys = new Set(
        mergedPreviewResults
          .filter((row) => !row.scratched)
          .map((row) => row.entryKey || row.riderUsef)
          .filter(Boolean)
      );
      for (const k of Object.keys(next)) {
        if (!keys.has(k)) delete next[k];
      }
      return next;
    });
  }, [mergedPreviewResults]);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (currentTab !== 1) return;
      if (!uploadResponse?.success) return;
      if (!hasMergeMissingPlacing(mergedClasses, deletedMergeClassIds)) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [currentTab, uploadResponse?.success, mergedClasses, deletedMergeClassIds]);

  // Reset showForm to true when upload fails (error exists, not uploading, no successful response)
  useEffect(() => {
    if (error && !uploading && !activeUploadResponse?.success) {
      setShowForm(true);
    }
  }, [error, uploading, activeUploadResponse]);


  // Extract judges from selected groups when both classes are selected
  useEffect(() => {
    if (!technicalAssessment || !qualityMarking || !activeUploadResponse?.groups) {
      setJudges([]);
      setJudgePositions({});
      return;
    }

    const judgeMap = new Map();

    // Get judges from technical assessment group
    const techGroup = activeUploadResponse.groups.find(
      (g) => g.id === technicalAssessment.id
    );
    const techJudges = techGroup?.judges || [];
    techJudges.forEach((judge) => {
      const judgeKey = `${judge.firstName || ""}_${judge.lastName || ""}`;
      if (!judgeMap.has(judgeKey) && (judge.firstName || judge.lastName)) {
        judgeMap.set(judgeKey, {
          id: judgeKey,
          firstName: judge.firstName || "",
          lastName: judge.lastName || "",
          USEFID: judge.USEFID || "",
        });
      }
    });

    // Get judges from quality marking group
    const qualityGroup = activeUploadResponse.groups.find(
      (g) => g.id === qualityMarking.id
    );
    const qualityJudges = qualityGroup?.judges || [];
    qualityJudges.forEach((judge) => {
      const judgeKey = `${judge.firstName || ""}_${judge.lastName || ""}`;
      if (!judgeMap.has(judgeKey) && (judge.firstName || judge.lastName)) {
        judgeMap.set(judgeKey, {
          id: judgeKey,
          firstName: judge.firstName || "",
          lastName: judge.lastName || "",
          USEFID: judge.USEFID || "",
        });
      }
    });

    const uniqueJudges = Array.from(judgeMap.values());
    setJudges(uniqueJudges);
    setJudgePositions({});
  }, [technicalAssessment, qualityMarking, activeUploadResponse?.groups]);

  // Reset form when classes change
  useEffect(() => {
    setJudgePositions({});
    setJudgePositionErrors(new Set());
  }, [technicalAssessment, qualityMarking]);

  // Clear selections if they become part of a merged class
  useEffect(() => {
    // Get all class IDs that are already in merged classes (excluding deleted ones)
    const mergedClassIds = new Set();
    mergedClasses.forEach((mergedClass) => {
      if (!deletedMergeClassIds.has(mergedClass.technicalAssessment.id)) {
        mergedClassIds.add(mergedClass.technicalAssessment.id);
      }
      if (!deletedMergeClassIds.has(mergedClass.qualityMarking.id)) {
        mergedClassIds.add(mergedClass.qualityMarking.id);
      }
    });

    // Clear technical assessment if it's in merged list
    if (technicalAssessment && mergedClassIds.has(technicalAssessment.id)) {
      setTechnicalAssessment(null);
    }

    // Clear quality marking if it's in merged list
    if (qualityMarking && mergedClassIds.has(qualityMarking.id)) {
      setQualityMarking(null);
    }
  }, [mergedClasses, deletedMergeClassIds, technicalAssessment, qualityMarking]);

  // Fetch merged preview results when both technical and quality classes are selected
  useEffect(() => {
    const fetchMergedPreview = async () => {
      if (!technicalAssessment?.id || !qualityMarking?.id) {
        setMergedPreviewResults([]);
        return;
      }

      const results = await fetchPreview(technicalAssessment.id, qualityMarking.id);
      setMergedPreviewResults(results);
    };

    fetchMergedPreview();
  }, [technicalAssessment?.id, qualityMarking?.id, fetchPreview]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      reset();
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      return;
    }

    try {
      setShowForm(false);
      await upload(selectedFile);
    } catch {
      // Error is handled by the hook
      setShowForm(true);
    }
  };

  const handleCancel = () => {
    navigate("/results");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      return format(new Date(dateString), "MM/dd/yyyy HH:mm");
    } catch (err) {
      console.error("Error formatting date:", err);
      return dateString;
    }
  };

  // Show loading spinner while uploading or loading existing results
  if (uploading && !uploadResponse) {
    return (
      <Box>
        <PageBreadcrumbs
          items={[
            { label: t("results.title"), href: "/results" },
            { label: t("results.upload.title") },
          ]}
        />
        <Paper sx={{ p: 4, maxWidth: 800, mx: "auto", mt: 2 }}>
          <Stack spacing={3}>
            {error && (
              <Alert severity="error" onClose={reset}>
                {typeof error === "object" &&
                  (error.type === "competitionStatus" ||
                    error.type === "ownershipError") ? (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      {t(error.messageKey)}
                    </Typography>
                    <Typography variant="body2">
                      {t(error.descriptionKey)}
                    </Typography>
                  </Box>
                ) : (
                  error
                )}
              </Alert>
            )}
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
              gap={2}
            >
              <CircularProgress size={48} />
              <Typography variant="body1" color="text.secondary">
                {t("results.upload.uploading")}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>
    );
  }

  // Show preview after successful upload or when in republish mode
  if (activeUploadResponse?.success && activeUploadResponse.groups) {
    // Sort groups: NEW/UPLOADED/ERROR at the top, then PUBLISHED, then SKIPPED/DNF at the bottom
    const sortedGroups = [...activeUploadResponse.groups].sort((a, b) => {
      const aIsSkipped =
        a.status === ResultGroupStatus.SKIPPED ||
        a.className === "(Did not Fill)";
      const bIsSkipped =
        b.status === ResultGroupStatus.SKIPPED ||
        b.className === "(Did not Fill)";

      const aIsNewOrUploadedOrError =
        a.status === ResultGroupStatus.NEW ||
        a.status === ResultGroupStatus.UPLOADED ||
        a.status === ResultGroupStatus.ERROR;
      const bIsNewOrUploadedOrError =
        b.status === ResultGroupStatus.NEW ||
        b.status === ResultGroupStatus.UPLOADED ||
        b.status === ResultGroupStatus.ERROR;

      // SKIPPED/DNF always go to bottom
      if (aIsSkipped && !bIsSkipped) return 1;
      if (!aIsSkipped && bIsSkipped) return -1;

      // NEW/UPLOADED/ERROR go to top
      if (aIsNewOrUploadedOrError && !bIsNewOrUploadedOrError) return -1;
      if (!aIsNewOrUploadedOrError && bIsNewOrUploadedOrError) return 1;

      // If both are same type (both new/uploaded/error or both published), sort by classId
      // Try to parse as numbers for numeric sorting, fall back to string comparison
      const aClassId = a.classId || "";
      const bClassId = b.classId || "";
      const aNum = parseInt(aClassId, 10);
      const bNum = parseInt(bClassId, 10);

      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum; // Numeric sort
      }
      return aClassId.localeCompare(bClassId); // String sort
    });

    return (
      <Box>
        <PageBreadcrumbs
          items={[
            { label: t("results.title"), href: "/results" },
            { label: t("results.upload.preview.breadcrumb") },
          ]}
        />

        <Paper sx={{ p: 4, maxWidth: 1200, mx: "auto" }}>
          <Stack spacing={3}>
            {uploading && (
              <Alert severity="info" icon={<CircularProgress size={20} />}>
                {t("results.upload.uploading")} - Results are being processed...
              </Alert>
            )}
            {error && (
              <Alert severity="error" onClose={reset}>
                {typeof error === "object" &&
                  (error.type === "competitionStatus" ||
                    error.type === "ownershipError") ? (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      {t(error.messageKey)}
                    </Typography>
                    <Typography variant="body2">
                      {t(error.descriptionKey)}
                    </Typography>
                  </Box>
                ) : (
                  error
                )}
              </Alert>
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Box>
                <Typography variant="h5" gutterBottom>
                  {t("results.upload.preview.title")}
                </Typography>
                {activeUploadResponse.show && (
                  <Box sx={{ mt: 1, mb: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {activeUploadResponse.show.CompName ||
                        t("results.upload.preview.noShowName")}
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>
                          {t("results.upload.preview.usefCompId")}:
                        </strong>{" "}
                        {activeUploadResponse.show.USEFCompID}
                      </Typography>
                      {activeUploadResponse.show.CompYear && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>
                            {t("results.upload.preview.compYear")}:
                          </strong>{" "}
                          {activeUploadResponse.show.CompYear}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                )}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {t("results.upload.preview.message")}
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={async () => {
                  if (sortedGroups.length === 0) {
                    return;
                  }
                  if (hasMergeMissingPlacing(mergedClasses, deletedMergeClassIds)) {
                    if (
                      !window.confirm(t("results.upload.merge.placingConfirmPublish"))
                    ) {
                      return;
                    }
                  }
                  try {
                    // Create actions array: 'publish' for selected, 'skip' for unselected
                    // Exclude SKIPPED groups from actions (they're already skipped)
                    const actions = sortedGroups
                      .filter(
                        (group) =>
                          group.status !== ResultGroupStatus.SKIPPED &&
                          group.className !== "(Did not Fill)"
                      )
                      .map((group) => {
                        const action = {
                          id: group.id,
                          action: selectedIds.has(group.id)
                            ? "publish"
                            : "skip",
                        };

                        // Check if this group had its merge deleted
                        if (deletedMergeClassIds.has(group.id)) {
                          // Explicitly clear merge data - we'll handle this in the backend
                          // by checking if merge property exists but is explicitly set to clear
                          action.clearMerge = true;
                        } else {
                          // Check if this group is part of a merged class
                          const mergedClass = mergedClasses.find(
                            (mc) =>
                              mc.technicalAssessment.id === group.id ||
                              mc.qualityMarking.id === group.id
                          );

                          if (mergedClass) {
                            if (group.id === mergedClass.technicalAssessment.id) {
                              action.merge = {
                                type: "technical",
                                mergeWith: mergedClass.qualityMarking.classId,
                                judges: mergedClass.judges.map((judge) => ({
                                  USEFID: judge.USEFID || "",
                                  firstName: judge.firstName || "",
                                  lastName: judge.lastName || "",
                                  position: String(judge.position || "")
                                    .trim()
                                    .toUpperCase(),
                                })),
                                placingsByRider: mergedClass.ridePlacings || {},
                              };
                            } else if (group.id === mergedClass.qualityMarking.id) {
                              // This is the quality marking class
                              action.merge = {
                                type: "quality",
                                mergeWith: mergedClass.technicalAssessment.classId,
                              };
                            }
                          }
                        }

                        return action;
                      });
                    await publish(actions);
                    // Redirect to results page for this competition
                    if (activeUploadResponse?.show?.USEFCompID && activeUploadResponse?.show?.CompYear) {
                      navigate(`/results/${activeUploadResponse.show.CompYear}/${activeUploadResponse.show.USEFCompID}`);
                    }
                  } catch {
                    // Error is handled by the hook
                  }
                }}
                disabled={
                  sortedGroups.filter(
                    (g) =>
                      g.status !== ResultGroupStatus.SKIPPED &&
                      g.className !== "(Did not Fill)"
                  ).length === 0 || uploading
                }
              >
                {uploading
                  ? t("results.upload.publishing")
                  : t("results.upload.preview.publishButton")}
              </Button>
            </Box>

            <Box>
              <Tabs
                value={currentTab}
                onChange={(e, newValue) => {
                  // Prevent switching to Merge tab while uploading or loading group details
                  if (newValue === 1 && uploading) {
                    return;
                  }
                  if (currentTab === 1 && newValue !== 1) {
                    if (hasMergeMissingPlacing(mergedClasses, deletedMergeClassIds)) {
                      if (
                        !window.confirm(
                          t("results.upload.merge.placingConfirmLeaveMergeTab")
                        )
                      ) {
                        return;
                      }
                    }
                  }
                  setCurrentTab(newValue);
                }}
                sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}
              >
                <Tab label="Class Results" />
                <Tab label="Merge Classes" disabled={uploading} />
              </Tabs>

              {currentTab === 0 && (
                <Box>
                  {(() => {
                    // Get all class IDs that are in merged classes (excluding deleted ones)
                    const mergedClassIds = new Set();
                    mergedClasses.forEach((mergedClass) => {
                      if (!deletedMergeClassIds.has(mergedClass.technicalAssessment.id)) {
                        mergedClassIds.add(mergedClass.technicalAssessment.id);
                      }
                      if (!deletedMergeClassIds.has(mergedClass.qualityMarking.id)) {
                        mergedClassIds.add(mergedClass.qualityMarking.id);
                      }
                    });

                    // Filter out merged classes from the display
                    const groupsWithoutMerged = sortedGroups.filter(
                      (group) => !mergedClassIds.has(group.id)
                    );

                    // Count skipped classes
                    const skippedGroups = groupsWithoutMerged.filter(
                      (group) =>
                        group.status === ResultGroupStatus.SKIPPED ||
                        group.className === "(Did not Fill)"
                    );
                    const skippedCount = skippedGroups.length;

                    // Filter out skipped classes from the display
                    const filteredGroups = groupsWithoutMerged.filter(
                      (group) =>
                        group.status !== ResultGroupStatus.SKIPPED &&
                        group.className !== "(Did not Fill)"
                    );

                    return (
                      <>
                        <Typography variant="h6" gutterBottom>
                          Class Results ({filteredGroups.length})
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    indeterminate={
                                      selectedIds.size > 0 &&
                                      selectedIds.size < filteredGroups.length
                                    }
                                    checked={
                                      filteredGroups.length > 0 &&
                                      selectedIds.size === filteredGroups.length
                                    }
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedIds(
                                          new Set(
                                            filteredGroups.map((group) => group.id)
                                          )
                                        );
                                      } else {
                                        setSelectedIds(new Set());
                                      }
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  {t("results.upload.preview.columns.classId")}
                                </TableCell>
                                <TableCell>
                                  {t("results.upload.preview.columns.className")}
                                </TableCell>
                                <TableCell>
                                  {t("results.upload.preview.columns.itemCount")}
                                </TableCell>
                                <TableCell>
                                  {t("results.upload.preview.columns.uploadedAt")}
                                </TableCell>
                                <TableCell>
                                  {t("results.upload.preview.columns.status")}
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredGroups.map((group) => {
                                // Treat UPLOADED and ERROR the same as NEW - show as NEW, don't grey out, keep checked
                                const isNewOrUploadedOrError =
                                  group.status === ResultGroupStatus.NEW ||
                                  group.status === ResultGroupStatus.UPLOADED ||
                                  group.status === ResultGroupStatus.ERROR;
                                return (
                                  <TableRow key={group.id}>
                                    <TableCell padding="checkbox">
                                      <Checkbox
                                        checked={selectedIds.has(group.id)}
                                        onChange={(e) => {
                                          const newSelected = new Set(selectedIds);
                                          if (e.target.checked) {
                                            newSelected.add(group.id);
                                          } else {
                                            newSelected.delete(group.id);
                                          }
                                          setSelectedIds(newSelected);
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell>{group.classId}</TableCell>
                                    <TableCell>{group.className || "-"}</TableCell>
                                    <TableCell>{group.itemCount}</TableCell>
                                    <TableCell>{formatDate(group.uploadedAt)}</TableCell>
                                    <TableCell>
                                      {group.status === ResultGroupStatus.ERROR
                                        ? t("results.upload.preview.reuploadFixErrors")
                                        : isNewOrUploadedOrError
                                          ? t("results.upload.preview.new")
                                          : t("results.upload.preview.reupload")}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                              {filteredGroups.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={6} align="center">
                                    {t("results.empty")}
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        {skippedCount > 0 && (
                          <Typography
                            variant="body2"
                            sx={{
                              mt: 1,
                              opacity: 0.5,
                              fontStyle: "italic",
                            }}
                          >
                            {skippedCount === 1
                              ? t("results.skippedClasses.single", { count: skippedCount })
                              : t("results.skippedClasses.plural", { count: skippedCount })}
                          </Typography>
                        )}
                      </>
                    );
                  })()}
                </Box>
              )}

              {currentTab === 1 && (
                <Box>
                  <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Merge Classes
                    </Typography>
                    <Stack spacing={3} sx={{ mt: 2 }}>
                      {(() => {
                        // Get all class IDs that are already in merged classes (excluding deleted ones)
                        const mergedClassIds = new Set();
                        mergedClasses.forEach((mergedClass) => {
                          if (!deletedMergeClassIds.has(mergedClass.technicalAssessment.id)) {
                            mergedClassIds.add(mergedClass.technicalAssessment.id);
                          }
                          if (!deletedMergeClassIds.has(mergedClass.qualityMarking.id)) {
                            mergedClassIds.add(mergedClass.qualityMarking.id);
                          }
                        });

                        const technicalOptions = buildMergeClassOptions(sortedGroups, {
                          mergedClassIds,
                          excludeOtherSelectionId: qualityMarking?.id,
                          suggestedSectionCodes: SUGGESTED_MERGE_SECTION_CODES,
                        });

                        const qualityOptions = buildMergeClassOptions(sortedGroups, {
                          mergedClassIds,
                          excludeOtherSelectionId: technicalAssessment?.id,
                          suggestedSectionCodes: SUGGESTED_MERGE_SECTION_CODES,
                        });

                        const mergeAutocompleteGroupBy = (option) =>
                          isSuggestedMergeSection(option, SUGGESTED_MERGE_SECTION_CODES)
                            ? t("results.upload.merge.suggestedGroup")
                            : t("results.upload.merge.otherGroup");

                        const technicalUseMergeGroups = mergeOptionsShouldUseGroupHeaders(
                          technicalOptions,
                          SUGGESTED_MERGE_SECTION_CODES
                        );
                        const qualityUseMergeGroups = mergeOptionsShouldUseGroupHeaders(
                          qualityOptions,
                          SUGGESTED_MERGE_SECTION_CODES
                        );

                        return (
                          <>
                            <Autocomplete
                              options={technicalOptions}
                              groupBy={
                                technicalUseMergeGroups ? mergeAutocompleteGroupBy : undefined
                              }
                              getOptionLabel={(option) =>
                                `${option.classId} - ${option.className || "-"}`
                              }
                              value={technicalAssessment}
                              onChange={(event, newValue) => {
                                // Prevent selecting a class that's in the merged list
                                if (newValue && mergedClassIds.has(newValue.id)) {
                                  return;
                                }
                                setTechnicalAssessment(newValue);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Technical Assessment"
                                  placeholder="Select a class result group"
                                />
                              )}
                              isOptionEqualToValue={(option, value) =>
                                option?.id === value?.id
                              }
                            />
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <Button
                                type="button"
                                variant="outlined"
                                size="small"
                                startIcon={<SwapHorizIcon />}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (technicalAssessment && qualityMarking) {
                                    // Swap the selections
                                    const temp = technicalAssessment;
                                    setTechnicalAssessment(qualityMarking);
                                    setQualityMarking(temp);
                                  }
                                }}
                                disabled={!technicalAssessment || !qualityMarking}
                                sx={{ minWidth: "auto" }}
                              >
                                Swap
                              </Button>
                            </Box>
                            <Autocomplete
                              options={qualityOptions}
                              groupBy={
                                qualityUseMergeGroups ? mergeAutocompleteGroupBy : undefined
                              }
                              getOptionLabel={(option) =>
                                `${option.classId} - ${option.className || "-"}`
                              }
                              value={qualityMarking}
                              onChange={(event, newValue) => {
                                // Prevent selecting a class that's in the merged list
                                if (newValue && mergedClassIds.has(newValue.id)) {
                                  return;
                                }
                                setQualityMarking(newValue);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Quality Marking"
                                  placeholder="Select a class result group"
                                />
                              )}
                              isOptionEqualToValue={(option, value) =>
                                option?.id === value?.id
                              }
                            />
                          </>
                        );
                      })()}
                    </Stack>

                    {/* Judges section - only show when both classes are selected */}
                    {technicalAssessment && qualityMarking && (
                      <Box sx={{ mt: 4 }}>
                        <Typography variant="h6" gutterBottom>
                          Judges
                        </Typography>
                        <Stack spacing={2} sx={{ mt: 2 }}>
                          {judges.map((judge) => {
                            const pos = String(judgePositions[judge.id] || "").trim();
                            const legacyOption =
                              pos !== "" && !JUDGE_POSITION_OPTIONS.includes(pos);
                            return (
                            <Box
                              key={judge.id}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{ minWidth: 200 }}
                              >
                                {judge.firstName} {judge.lastName}
                              </Typography>
                              <TextField
                                select
                                required
                                size="small"
                                sx={{ minWidth: 120 }}
                                error={judgePositionErrors.has(judge.id)}
                                label={t("results.upload.merge.judgePosition")}
                                value={pos}
                                onChange={(e) => {
                                  const value = String(e.target.value || "").toUpperCase();
                                  setJudgePositions((prev) => ({
                                    ...prev,
                                    [judge.id]: value,
                                  }));
                                  if (judgePositionErrors.has(judge.id)) {
                                    setJudgePositionErrors((prev) => {
                                      const newSet = new Set(prev);
                                      newSet.delete(judge.id);
                                      return newSet;
                                    });
                                  }
                                }}
                                SelectProps={{
                                  renderValue: (selected) => {
                                    if (selected == null || selected === "") {
                                      return (
                                        <Typography
                                          component="span"
                                          color="text.secondary"
                                          sx={{ fontStyle: "italic" }}
                                        >
                                          {t("results.upload.merge.judgePositionSelect")}
                                        </Typography>
                                      );
                                    }
                                    return selected;
                                  },
                                }}
                              >
                                {legacyOption && (
                                  <MenuItem value={pos}>{pos}</MenuItem>
                                )}
                                {JUDGE_POSITION_OPTIONS.map((letter) => (
                                  <MenuItem key={letter} value={letter}>
                                    {letter}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Box>
                            );
                          })}
                          {judges.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                              No judges found in selected classes
                            </Typography>
                          )}
                        </Stack>

                        {/* Merged Preview Results Table */}
                        {loadingPreview ? (
                          <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : mergedPreviewResults.length > 0 ? (
                          <Box sx={{ mt: 4 }}>
                            <Typography variant="h6" gutterBottom>
                              Merged Preview Results
                            </Typography>
                            <TableContainer sx={{ mt: 2 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ width: 40 }} />
                                    <TableCell>Rider</TableCell>
                                    <TableCell>Horse</TableCell>
                                    <TableCell sx={{ minWidth: 100 }}>
                                      {t("results.upload.merge.ridePlacingColumn")}
                                    </TableCell>
                                    <TableCell align="right">Technical Score</TableCell>
                                    <TableCell align="right">Quality Score</TableCell>
                                    <TableCell align="right">Technical %</TableCell>
                                    <TableCell align="right">Quality %</TableCell>
                                    <TableCell align="right">Total %</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {mergedPreviewResults.map((result, index) => {
                                    const rk = result.entryKey || result.riderUsef || "";
                                    const rowKey = rk || `preview-row-${index}`;
                                    const expanded = expandedPreviewRows.has(rowKey);
                                    const scratchedSx = result.scratched
                                      ? { "& td, & th": { fontStyle: "italic", color: "text.disabled" } }
                                      : undefined;
                                    return (
                                      <Fragment key={rowKey}>
                                        <TableRow sx={scratchedSx}>
                                          <TableCell sx={{ width: 40 }}>
                                            <IconButton
                                              size="small"
                                              aria-label={
                                                expanded
                                                  ? t("results.upload.merge.hideUsefDetails")
                                                  : t("results.upload.merge.showUsefDetails")
                                              }
                                              onClick={() =>
                                                setExpandedPreviewRows((prev) => {
                                                  const next = new Set(prev);
                                                  if (next.has(rowKey)) {
                                                    next.delete(rowKey);
                                                  } else {
                                                    next.add(rowKey);
                                                  }
                                                  return next;
                                                })
                                              }
                                            >
                                              {expanded ? (
                                                <ExpandLessIcon fontSize="small" />
                                              ) : (
                                                <ExpandMoreIcon fontSize="small" />
                                              )}
                                            </IconButton>
                                          </TableCell>
                                          <TableCell>
                                            {result.firstName} {result.lastName}
                                          </TableCell>
                                          <TableCell>{result.horseName || "-"}</TableCell>
                                          <TableCell>
                                            {result.scratched ? "—" : rk ? (
                                              <TextField
                                                size="small"
                                                value={previewRidePlacings[rk] ?? ""}
                                                onChange={(e) =>
                                                  setPreviewRidePlacings((p) => ({
                                                    ...p,
                                                    [rk]: e.target.value,
                                                  }))
                                                }
                                                placeholder={t(
                                                  "results.upload.merge.placingPlaceholder"
                                                )}
                                                sx={{ minWidth: 96 }}
                                              />
                                            ) : (
                                              "—"
                                            )}
                                          </TableCell>
                                          <TableCell align="right">
                                            {result.scratched ? "(scratched)" : formatScoreOrPercent3(result.technicalScore)}
                                          </TableCell>
                                          <TableCell align="right">
                                            {result.scratched ? "" : formatScoreOrPercent3(result.qualityScore)}
                                          </TableCell>
                                          <TableCell align="right">
                                            {result.scratched ? "" : formatScoreOrPercent3(result.technicalPercentage)}
                                          </TableCell>
                                          <TableCell align="right">
                                            {result.scratched ? "" : formatScoreOrPercent3(result.qualityPercentage)}
                                          </TableCell>
                                          <TableCell align="right">
                                            {result.scratched ? "" : formatScoreOrPercent3(result.totalPercentage)}
                                          </TableCell>
                                        </TableRow>
                                        <TableRow>
                                          <TableCell
                                            sx={{ py: 0, borderBottom: expanded ? undefined : "none" }}
                                            colSpan={9}
                                          >
                                            <Collapse in={expanded} timeout="auto" unmountOnExit>
                                              <MergedRideUsefDetail result={result} t={t} />
                                            </Collapse>
                                          </TableCell>
                                        </TableRow>
                                      </Fragment>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        ) : null}

                        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                          <Button
                            variant="contained"
                            onClick={() => {
                              // Find judges with missing positions
                              const missingPositionJudges = new Set();
                              judges.forEach((judge) => {
                                if (!judgePositions[judge.id] || judgePositions[judge.id].trim() === "") {
                                  missingPositionJudges.add(judge.id);
                                }
                              });

                              // Set error state for judges with missing positions
                              if (missingPositionJudges.size > 0) {
                                setJudgePositionErrors(missingPositionJudges);
                                return; // Don't merge if positions are missing
                              }

                              // Clear any previous errors
                              setJudgePositionErrors(new Set());

                              const mergedClass = {
                                id: `merged_${Date.now()}`,
                                technicalAssessment: {
                                  id: technicalAssessment.id,
                                  classId: technicalAssessment.classId,
                                  className: technicalAssessment.className,
                                },
                                qualityMarking: {
                                  id: qualityMarking.id,
                                  classId: qualityMarking.classId,
                                  className: qualityMarking.className,
                                },
                                judges: judges.map((judge) => ({
                                  ...judge,
                                  position: String(judgePositions[judge.id] || "")
                                    .trim()
                                    .toUpperCase(),
                                })),
                                ridePlacings: { ...previewRidePlacings },
                              };
                              setMergedClasses((prev) => [...prev, mergedClass]);
                              // Reset form
                              setTechnicalAssessment(null);
                              setQualityMarking(null);
                              setJudges([]);
                              setJudgePositions({});
                              setJudgePositionErrors(new Set());
                            }}
                          >
                            Merge
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Paper>

                  {/* Merged classes list */}
                  {mergedClasses.length > 0 && (
                    <Paper variant="outlined" sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        Merged Classes
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t("results.upload.merge.placingNotFromFile")}
                      </Typography>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Technical Assessment</TableCell>
                              <TableCell>Quality Marking</TableCell>
                              <TableCell>Judges</TableCell>
                              <TableCell align="center">
                                {t("results.upload.merge.ridePlacings")}
                              </TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {mergedClasses.map((mergedClass) => (
                              <TableRow key={mergedClass.id}>
                                <TableCell>
                                  {mergedClass.technicalAssessment.classId} -{" "}
                                  {mergedClass.technicalAssessment.className}
                                </TableCell>
                                <TableCell>
                                  {mergedClass.qualityMarking.classId} -{" "}
                                  {mergedClass.qualityMarking.className}
                                </TableCell>
                                <TableCell>
                                  <Stack spacing={0.75} alignItems="flex-start">
                                    {mergedClass.judges.map((j, idx) => (
                                      <Typography
                                        key={j.USEFID || `judge-${idx}-${j.firstName}-${j.lastName}`}
                                        variant="body2"
                                      >
                                        {j.firstName} {j.lastName} (
                                        {formatJudgePositionLetter(j.position)})
                                      </Typography>
                                    ))}
                                  </Stack>
                                </TableCell>
                                <TableCell align="center">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={async () => {
                                      setRidePlacingDialogMergeId(mergedClass.id);
                                      setDialogPlacingLoading(true);
                                      setDialogPreviewRows([]);
                                      setDialogRidePlacings({});
                                      try {
                                        const rows = await fetchPreview(
                                          mergedClass.technicalAssessment.id,
                                          mergedClass.qualityMarking.id
                                        );
                                        const init = {};
                                        for (const r of rows) {
                                          if (r.scratched) continue;
                                          const k = r.entryKey || r.riderUsef || "";
                                          if (!k) continue;
                                          init[k] =
                                            mergedClass.ridePlacings?.[k] != null
                                              ? String(mergedClass.ridePlacings[k])
                                              : String(r.placing ?? "").trim();
                                        }
                                        setDialogRidePlacings(init);
                                        setDialogPreviewRows(rows);
                                      } finally {
                                        setDialogPlacingLoading(false);
                                      }
                                    }}
                                  >
                                    {t("results.upload.merge.editRidePlacings")}
                                  </Button>
                                </TableCell>
                                <TableCell align="right">
                                  <Button
                                    size="small"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => {
                                      setDeletedMergeClassIds((prev) => {
                                        const newSet = new Set(prev);
                                        newSet.add(mergedClass.technicalAssessment.id);
                                        newSet.add(mergedClass.qualityMarking.id);
                                        return newSet;
                                      });
                                      setMergedClasses((prev) =>
                                        prev.filter((c) => c.id !== mergedClass.id)
                                      );
                                    }}
                                  >
                                    Delete
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Dialog
                        open={ridePlacingDialogMergeId != null}
                        onClose={() => setRidePlacingDialogMergeId(null)}
                        maxWidth="md"
                        fullWidth
                      >
                        <DialogTitle>
                          {t("results.upload.merge.editRidePlacings")}
                        </DialogTitle>
                        <DialogContent>
                          {dialogPlacingLoading ? (
                            <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
                              <CircularProgress size={32} />
                            </Box>
                          ) : (
                            <TableContainer sx={{ mt: 1 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Rider</TableCell>
                                    <TableCell>Horse</TableCell>
                                    <TableCell sx={{ minWidth: 120 }}>
                                      {t("results.upload.merge.ridePlacingColumn")}
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {dialogPreviewRows.map((row, idx) => {
                                    const rk = row.entryKey || row.riderUsef || "";
                                    const scratchedSx = row.scratched
                                      ? { "& td, & th": { fontStyle: "italic", color: "text.disabled" } }
                                      : undefined;
                                    return (
                                      <TableRow key={rk || `dlg-${idx}`} sx={scratchedSx}>
                                        <TableCell>
                                          {row.firstName} {row.lastName}
                                        </TableCell>
                                        <TableCell>{row.horseName || "—"}</TableCell>
                                        <TableCell>
                                          {row.scratched ? (
                                            <span>(scratched)</span>
                                          ) : rk ? (
                                            <TextField
                                              size="small"
                                              fullWidth
                                              value={dialogRidePlacings[rk] ?? ""}
                                              onChange={(e) =>
                                                setDialogRidePlacings((p) => ({
                                                  ...p,
                                                  [rk]: e.target.value,
                                                }))
                                              }
                                              placeholder={t(
                                                "results.upload.merge.placingPlaceholder"
                                              )}
                                            />
                                          ) : (
                                            "—"
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                        </DialogContent>
                        <DialogActions>
                          <Button onClick={() => setRidePlacingDialogMergeId(null)}>
                            {t("results.upload.merge.ridePlacingDialogCancel")}
                          </Button>
                          <Button
                            variant="contained"
                            disabled={dialogPlacingLoading}
                            onClick={() => {
                              if (!ridePlacingDialogMergeId) return;
                              setMergedClasses((prev) =>
                                prev.map((c) =>
                                  c.id === ridePlacingDialogMergeId
                                    ? {
                                        ...c,
                                        ridePlacings: { ...dialogRidePlacings },
                                      }
                                    : c
                                )
                              );
                              setRidePlacingDialogMergeId(null);
                            }}
                          >
                            {t("results.upload.merge.ridePlacingDialogSave")}
                          </Button>
                        </DialogActions>
                      </Dialog>
                    </Paper>
                  )}
                </Box>
              )}
            </Box>
          </Stack>
        </Paper>
      </Box>
    );
  }

  // Show form with error if upload failed
  // If there's an error and we're not uploading and no successful response, show the form
  const shouldShowForm = showForm || (error && !uploading && !activeUploadResponse?.success);

  if (!shouldShowForm) {
    return null;
  }

  return (
    <Box>
      <PageBreadcrumbs
        items={[
          { label: t("results.title"), href: "/results" },
          { label: t("results.upload.title") },
        ]}
      />

      <Paper sx={{ p: 4, maxWidth: 800, mx: "auto" }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h5" gutterBottom>
              {t("results.upload.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("results.upload.description")}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" onClose={reset}>
              {typeof error === "object" &&
                (error.type === "competitionStatus" ||
                  error.type === "ownershipError") ? (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                    {t(error.messageKey)}
                  </Typography>
                  <Typography variant="body2">
                    {t(error.descriptionKey)}
                  </Typography>
                </Box>
              ) : (
                error
              )}
            </Alert>
          )}

          <Box>
            <Typography variant="body2" gutterBottom>
              {t("results.upload.fileLabel")}
            </Typography>
            <Box
              sx={{
                border: "2px dashed",
                borderColor: selectedFile ? "primary.main" : "grey.300",
                borderRadius: 2,
                p: 3,
                textAlign: "center",
                backgroundColor: selectedFile
                  ? "action.selected"
                  : "background.paper",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "action.hover",
                },
              }}
            >
              <input
                accept=".csv,.dat"
                style={{ display: "none" }}
                id="file-upload-input"
                type="file"
                onChange={handleFileChange}
                disabled={uploading || !showForm}
              />
              <label htmlFor="file-upload-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  disabled={uploading}
                  sx={{ mb: 2 }}
                >
                  {t("results.upload.selectFile")}
                </Button>
              </label>
              {selectedFile && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="primary">
                    <UploadFileIcon sx={{ verticalAlign: "middle", mr: 1 }} />
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(selectedFile.size / 1024).toFixed(2)} KB
                  </Typography>
                </Box>
              )}
              {!selectedFile && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {t("results.upload.fileHelper")}
                </Typography>
              )}
            </Box>
          </Box>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleCancel}
              disabled={uploading}
            >
              {t("results.upload.cancel")}
            </Button>
            <Button
              variant="contained"
              startIcon={uploading ? null : <UploadFileIcon />}
              onClick={handleUpload}
              disabled={uploading || !selectedFile}
            >
              {uploading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} />
                  {t("results.upload.uploading")}
                </Box>
              ) : (
                t("results.upload.uploadButton")
              )}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}

/** Translation key for the rule that produced a ride's TechnicalComponent. */
const TECHNICAL_RULE_KEY = {
  "judge-at-c": "results.upload.merge.ruleJudgeAtC",
  "panel-mean": "results.upload.merge.ruleMean",
  "single-judge": "results.upload.merge.ruleSingleJudge",
};

/**
 * (#1413) Score-only fallback copy for when `technical.percentageRule` does
 * not agree with `rule` — dropping the "and percentage" claim rather than
 * asserting a basis the percentage doesn't actually have. No `single-judge`
 * entry: mergeTechnicalQuality.js's explainTechnicalQualityMerge always sets
 * `percentageRule` equal to `rule` for "single-judge" (#1419 AC5 — there is
 * no panel to fall back from, so the row's own value IS that judge's own),
 * so that case never reaches this fallback.
 */
const TECHNICAL_SCORE_ONLY_RULE_KEY = {
  "judge-at-c": "results.upload.merge.ruleJudgeAtCScoreOnly",
  "panel-mean": "results.upload.merge.ruleMeanScoreOnly",
};

/**
 * Per-ride judges table: who scored, where they sat, and their raw score/percent.
 * Card #1378 — these are the literal inputs behind the USEF fields below, not a
 * recomputation of them.
 */
function MergedRideJudgesTable({ title, judges, t }) {
  if (!judges || judges.length === 0) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle2">{title}</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t("results.upload.merge.judgeColumn")}</TableCell>
            <TableCell>{t("results.upload.merge.judgePositionColumn")}</TableCell>
            <TableCell align="right">{t("results.upload.merge.judgeScoreColumn")}</TableCell>
            <TableCell align="right">{t("results.upload.merge.judgePercentColumn")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {judges.map((judge, i) => (
            <TableRow key={judge.usefId || `${judge.firstName}-${judge.lastName}-${i}`}>
              <TableCell>
                {judge.firstName} {judge.lastName}
              </TableCell>
              <TableCell>{judge.position || "—"}</TableCell>
              <TableCell align="right">{formatScoreOrPercent3(judge.score)}</TableCell>
              <TableCell align="right">{formatScoreOrPercent3(judge.percentage)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

/**
 * The literal USEF payload for one ride, its judge inputs, and the arithmetic spelled
 * out — derived entirely from what `getMergedRidesPreview` returns (itself derived from
 * `createUSEFClassData`/`mergeQualityIntoTechnical`, the exact submission-path functions).
 * Card #1378: nothing here is computed client-side, it only renders the response.
 */
function MergedRideUsefDetail({ result, t }) {
  const usef = result.usef || {};
  const technical = result.technical;
  const quality = result.quality;
  const arithmetic = result.arithmetic;

  return (
    <Box sx={{ my: 2, ml: 5 }}>
      <Typography variant="subtitle1" gutterBottom>
        {t("results.upload.merge.usefPayloadTitle")}
      </Typography>
      <Table size="small" sx={{ maxWidth: 480 }}>
        <TableBody>
          <TableRow>
            <TableCell>{t("results.upload.merge.usefFieldTechnicalComponent")}</TableCell>
            <TableCell align="right">{formatScoreOrPercent3(usef.TechnicalComponent)}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>{t("results.upload.merge.usefFieldQualityMarking")}</TableCell>
            <TableCell align="right">{formatScoreOrPercent3(usef.QualityMarking)}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>{t("results.upload.merge.usefFieldTotalDressageScore")}</TableCell>
            <TableCell align="right">{formatScoreOrPercent3(usef.TotalDressageScore)}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>{t("results.upload.merge.usefFieldTotalDressagePercentage")}</TableCell>
            <TableCell align="right">{formatScoreOrPercent3(usef.TotalDressagePercentage)}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>{t("results.upload.merge.usefFieldQualityPercentage")}</TableCell>
            <TableCell align="right">{formatScoreOrPercent3(usef.QualityPercentage)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>

      {/* (#1413, #1419 AC5) The "and percentage" claim is only true when the
          API's `percentageRule` agrees with `rule` — a
          C-with-Score-but-no-Percentage panel falls back to the row's own
          TotalDressagePercentage, a different basis than the score
          (mergeTechnicalQuality.js's explainTechnicalQualityMerge doc, #1399
          blocker 2). This reads the API's own model with NO special case:
          `percentageRule` already equals `rule` for "single-judge" rows
          (there is no panel to fall back from, so the row's own value IS
          that judge's own), so this component never has to infer that itself.
          A mismatch falls back to score-only wording; if a future rule value
          has no score-only copy either, this renders nothing rather than
          reasserting the "and percentage" claim it just proved false. */}
      {technical && TECHNICAL_RULE_KEY[technical.rule] && (
        technical.percentageRule === technical.rule ? (
          <Alert severity="info" sx={{ mt: 2, maxWidth: 480 }}>
            {t(TECHNICAL_RULE_KEY[technical.rule])}
          </Alert>
        ) : (
          TECHNICAL_SCORE_ONLY_RULE_KEY[technical.rule] && (
            <Alert severity="info" sx={{ mt: 2, maxWidth: 480 }}>
              {t(TECHNICAL_SCORE_ONLY_RULE_KEY[technical.rule])}
            </Alert>
          )
        )
      )}

      <MergedRideJudgesTable
        title={t("results.upload.merge.technicalJudgesTitle")}
        judges={technical?.judges}
        t={t}
      />
      <MergedRideJudgesTable
        title={t("results.upload.merge.qualityJudgesTitle")}
        judges={quality?.judges}
        t={t}
      />

      {arithmetic && (arithmetic.scoreLine || arithmetic.percentageLine) && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle2">{t("results.upload.merge.arithmeticTitle")}</Typography>
          {arithmetic.scoreLine && (
            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
              {arithmetic.scoreLine}
            </Typography>
          )}
          {arithmetic.percentageLine && (
            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
              {arithmetic.percentageLine}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

export default UploadResultsPage;
